import React, { useState, useEffect } from 'react';
import { usePrompt } from '../../hooks/useModals';
import { eventsService } from '../../services';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { LoadingSpinner } from '../ui/Loading';
import PromptModal from '../ui/PromptModal';

const AdminProofReviewModal = ({ isOpen, onClose, onReviewComplete }) => {
  const { modalState: promptState, prompt, close: closePrompt } = usePrompt();
  const [pendingProofs, setPendingProofs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [error, setError] = useState('');

  const loadPendingProofs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await eventsService.getPendingProofs();
      setPendingProofs(response.data || []);
    } catch (err) {
      setError('Erro ao carregar provas pendentes');
      console.error('Error loading pending proofs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (participationId, approved, rejectionReason = '') => {
    try {
      setReviewingId(participationId);
      setError('');
      
      await eventsService.reviewProof(participationId, {
        approved,
        rejectionReason
      });
      
      // Remove from pending list
      setPendingProofs(prev => 
        prev.filter(proof => proof.id !== participationId)
      );
      
      onReviewComplete && onReviewComplete();
    } catch (err) {
      setError('Erro ao processar revisão');
      console.error('Error reviewing proof:', err);
    } finally {
      setReviewingId(null);
    }
  };

  const handleApprove = (participationId) => {
    handleReview(participationId, true);
  };

  const handleReject = async (participationId) => {
    const reason = await prompt({
      title: 'Rejeitar Prova',
      message: 'Motivo da rejeição (opcional):',
      placeholder: 'Digite o motivo da rejeição...',
      confirmText: 'Rejeitar',
      cancelText: 'Cancelar',
      type: 'textarea',
      icon: '❌'
    });

    if (reason !== null) { // User didn't cancel
      handleReview(participationId, false, reason);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadPendingProofs();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-6xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white">
                🛡️
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Revisar Provas de Participação
                </h2>
                <p className="text-gray-600 text-sm">
                  {pendingProofs.length} provas aguardando revisão
                </p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full w-8 h-8 flex items-center justify-center transition-colors text-2xl"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <span className="text-red-500">⚠️</span>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : pendingProofs.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Todas as provas foram revisadas!
                </h3>
                <p className="text-gray-600">
                  Não há provas de participação aguardando revisão no momento.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pendingProofs.map((proof) => (
                  <div key={proof.id} className="bg-white border border-gray-200 rounded-2xl p-6">
                    {/* Event Info */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">🎯</span>
                        <h3 className="font-semibold text-white">
                          {proof.event.name}
                        </h3>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>📅 {new Date(proof.event.startDate).toLocaleDateString('pt-BR')}</p>
                        <p>🏆 {proof.event.points} pontos</p>
                        <p>🎮 Tipo: {proof.event.type}</p>
                      </div>
                    </div>

                    {/* Participant Info */}
                    <div className="mb-4 bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">
                            {proof.character.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-white">
                            {proof.character.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {proof.user.name} • Nv. {proof.character.level}
                          </div>
                        </div>
                      </div>
                      <Badge variant="warning" size="xs">
                        📸 Prova Enviada
                      </Badge>
                    </div>

                    {/* Proof Image */}
                    <div className="mb-6">
                      <h4 className="font-medium text-white mb-2">📷 Prova de Participação:</h4>
                      <div className="bg-gray-100 rounded-xl p-2">
                        <img
                          src={proof.proofImageUrl}
                          alt="Prova de participação"
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div 
                          className="hidden text-center text-gray-500 py-8"
                        >
                          ⚠️ Erro ao carregar imagem
                          <br />
                          <a 
                            href={proof.proofImageUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 underline text-sm"
                          >
                            Abrir link original
                          </a>
                        </div>
                      </div>
                      <a
                        href={proof.proofImageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 inline-block"
                      >
                        🔗 Abrir em nova aba
                      </a>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handleApprove(proof.id)}
                        variant="success"
                        size="sm"
                        disabled={reviewingId === proof.id}
                        loading={reviewingId === proof.id}
                        className="flex-1"
                      >
                        {reviewingId === proof.id ? 'Aprovando...' : '✅ Aprovar'}
                      </Button>
                      <Button
                        onClick={() => handleReject(proof.id)}
                        variant="danger"
                        size="sm"
                        disabled={reviewingId === proof.id}
                        className="flex-1"
                      >
                        ❌ Rejeitar
                      </Button>
                    </div>

                    {/* Submission Info */}
                    <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                      📤 Enviado em: {new Date(proof.updatedAt).toLocaleString('pt-BR')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              💡 Dica: Clique em "Aprovar" para conceder os pontos automaticamente
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={loadPendingProofs}
                variant="secondary"
                size="sm"
                disabled={loading}
              >
                🔄 Atualizar
              </Button>
              <Button onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Prompt Modal */}
      <PromptModal
        isOpen={promptState.isOpen}
        onClose={closePrompt}
        onConfirm={promptState.onConfirm}
        title={promptState.title}
        message={promptState.message}
        placeholder={promptState.placeholder}
        confirmText={promptState.confirmText}
        cancelText={promptState.cancelText}
        type={promptState.type}
        required={promptState.required}
        icon={promptState.icon}
      />
    </div>
  );
};

export default AdminProofReviewModal;